import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHeart, FaUsers, FaLeaf, FaPhone, FaClock, FaWhatsapp } from 'react-icons/fa';

const PlanosContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  @media screen and (max-width: 768px) {
    padding-top: 40px;
  }
`;

const PlanosWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px 40px 24px;
`;

const PlanosHeader = styled.div`
  margin-bottom: 40px;
`;

const TopLine = styled(motion.p)`
  color: #4CAF50;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  margin-bottom: 16px;
  font-family: 'Open Sans', sans-serif;
`;

const Heading = styled(motion.h1)`
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 24px;
  font-family: 'Playfair Display', serif;

  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.6;
  color: #333;
  margin: 0 auto 40px;
  font-family: 'Open Sans', sans-serif;
`;

const PlanosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 60px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const PlanoCard = styled(motion.div)`
  background: #fff;
  border-radius: 15px;
  padding: 40px 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border-top: 5px solid #4CAF50;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
`;

const PlanoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--primary-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #fff;
  font-size: 2rem;
`;

const PlanoTitle = styled.h3`
  font-size: 1.8rem;
  color: var(--primary-blue);
  margin-bottom: 20px;
  text-align: center;
  font-family: 'Playfair Display', serif;
`;

const PlanoDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 25px;
  font-family: 'Open Sans', sans-serif;
`;

const PlanoFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
`;

const PlanoFeature = styled.li`
  padding: 8px 0;
  color: #333;
  font-family: 'Open Sans', sans-serif;
  position: relative;
  padding-left: 25px;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #4CAF50;
    font-weight: bold;
    font-size: 1.1rem;
  }
`;

const ContactInfo = styled.div`
  background: var(--primary-blue);
  color: #fff;
  padding: 40px;
  border-radius: 15px;
  text-align: center;
  margin-top: 40px;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Playfair Display', serif;
`;

const ContactText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 20px;
  font-family: 'Open Sans', sans-serif;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 30px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-family: 'Open Sans', sans-serif;
  cursor: pointer
`;

const ContactIcon = styled.div`
  color: #4CAF50;
  font-size: 1.2rem;
`;

const ContactNumber = styled.span`
  font-weight: bold;
  font-size: 1.1rem;
`;

const Planos = () => {
  return (
    <PlanosContainer id="planos">
      <PlanosWrapper>
        <PlanosHeader>
          <TopLine
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Nossos Planos
          </TopLine>
          <Heading
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Uma Rede de Cuidado e Respeito em Todos os Momentos
          </Heading>
          <Subtitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Com uma trajetória marcada pela confiança e pelo cuidado, construímos ao longo dos anos uma base sólida de serviços voltados à dignidade e ao amparo das famílias. Agora, damos um novo passo: ampliar nossa missão para promover bem-estar em todas as fases da vida.
          </Subtitle>
          <Subtitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            Nosso foco vai além da despedida, queremos estar ao seu lado para oferecer tranquilidade, proteção e qualidade de vida todos os dias. Por isso, investimos em soluções completas, com assistências e benefícios pensados para garantir segurança e conforto, permitindo que você aproveite ao máximo cada momento com quem mais ama.
          </Subtitle>
        </PlanosHeader>

        <PlanosGrid>
          <PlanoCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <PlanoIcon>
              <FaLeaf />
            </PlanoIcon>
            <PlanoTitle>Plano Cremação</PlanoTitle>
            <PlanoDescription>
              No plano de Cremação disponibilizamos uma solução completa e digna para o momento da despedida.
            </PlanoDescription>
            <PlanoFeatures>
              <PlanoFeature>Capela para Velar</PlanoFeature>
              <PlanoFeature>Capela nobre para Cerimonial de Despedida</PlanoFeature>
              <PlanoFeature>Urna para Restos Cremados (modelo padrão simples)</PlanoFeature>
              <PlanoFeature>Enfermeira para acompanhamento dos familiares</PlanoFeature>
              <PlanoFeature>Estrutura completa</PlanoFeature>
            </PlanoFeatures>
            <PlanoDescription>
              Temos ainda outras opções de planos de cremação. Entre em contato para maiores informações.
            </PlanoDescription>
          </PlanoCard>

          <PlanoCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <PlanoIcon>
              <FaUsers />
            </PlanoIcon>
            <PlanoTitle>Plano Familiar</PlanoTitle>
            <PlanoDescription>
              O Plano Familiar engloba toda a família com benefícios exclusivos e assistência completa.
            </PlanoDescription>
            <PlanoFeatures>
              <PlanoFeature>Cobertura para pai, mãe, filhos menores</PlanoFeature>
              <PlanoFeature>Inclui incapazes, sogro e sogra</PlanoFeature>
              <PlanoFeature>Direito a ambulância</PlanoFeature>
              <PlanoFeature>Cadeira de roda, bengala, colchão piramidal</PlanoFeature>
              <PlanoFeature>Mais capelas para velar</PlanoFeature>
              <PlanoFeature>Ônibus para cortejo</PlanoFeature>
              <PlanoFeature>Funeral completo gratuitamente</PlanoFeature>
            </PlanoFeatures>
          </PlanoCard>

          <PlanoCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <PlanoIcon>
              <FaHeart />
            </PlanoIcon>
            <PlanoTitle>Outros Planos</PlanoTitle>
            <PlanoDescription>
              Disponibilizamos também planos especializados para diferentes necessidades.
            </PlanoDescription>
            <PlanoFeatures>
              <PlanoFeature>Cemitério Convencional</PlanoFeature>
              <PlanoFeature>Cemitério Jardim</PlanoFeature>
              <PlanoFeature>Gavetas</PlanoFeature>
              <PlanoFeature>Planos personalizados</PlanoFeature>
              <PlanoFeature>Assistência completa</PlanoFeature>
            </PlanoFeatures>
            <PlanoDescription>
              Entre em contato para maiores informações.
            </PlanoDescription>
          </PlanoCard>
        </PlanosGrid>

        <ContactInfo
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <ContactTitle>Entre em Contato</ContactTitle>
          <ContactText>
            Para mais informações sobre nossos planos e serviços, entre em contato conosco:
          </ContactText>
          <ContactGrid>
            <ContactItem onClick={() => {
                const message = encodeURIComponent('Olá, gostaria de mais informações acerca dos planos.');
                window.open(`https://wa.me/555535121297?text=${message}`);
            }}>
              <ContactIcon>
                <FaWhatsapp />
              </ContactIcon>
              <ContactNumber>(55) 3512-1297</ContactNumber>
            </ContactItem>
            <ContactItem onClick={() => window.open('tel:+555535121297')}>
              <ContactIcon>
                <FaClock />
              </ContactIcon>
              <ContactNumber>Plantão 24h: (55) 3512-1297</ContactNumber>
            </ContactItem>
            <ContactItem onClick={() => window.open('tel:+555535122727')}>
              <ContactIcon style={{rotate: '90deg'}}>
                <FaPhone />
              </ContactIcon>
              <ContactNumber>(55) 3512-2727</ContactNumber>
            </ContactItem>
          </ContactGrid>
        </ContactInfo>
      </PlanosWrapper>
    </PlanosContainer>
  );
};

export default Planos; 